import { useEffect, useRef } from 'react';
import { PAPER_DOTS_80MM, renderPrintPreview } from '@/lib/receipt-logo';

/**
 * A mock 80mm receipt with the chosen logo at the top.
 *
 * The logo on its own tells a merchant how it dithers, but not whether it
 * works as a receipt header — whether it overpowers the store name, or is so
 * small it may as well not be there. This shows it in proportion: the mock is
 * PAPER_DOTS_80MM wide, so the logo occupies exactly the share of the paper it
 * will occupy in real life.
 *
 * The body text is representative filler, not the merchant's real data — this
 * is about the header, and inventing plausible-looking totals would only
 * distract from it.
 */
export function ReceiptSamplePreview({
  logo,
  storeName,
  addressLine,
  cityLine,
}: {
  logo: HTMLImageElement | null;
  storeName: string;
  addressLine?: string;
  cityLine?: string;
}) {
  const logoHost = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = logoHost.current;
    if (!host) return;
    host.replaceChildren();
    if (!logo) return;
    const canvas = renderPrintPreview(logo, PAPER_DOTS_80MM);
    // Percentage width keeps the logo proportional to the paper however the
    // mock is scaled to fit the panel.
    canvas.style.width = `${(canvas.width / PAPER_DOTS_80MM) * 100}%`;
    canvas.style.height = 'auto';
    // Nearest-neighbour: smoothing would blur black-and-white pixels into
    // greys the printer cannot produce, flattering the preview.
    canvas.style.imageRendering = 'pixelated';
    canvas.style.display = 'block';
    canvas.style.margin = '0 auto';
    host.appendChild(canvas);
  }, [logo]);

  return (
    <div className="w-[300px] shrink-0 rounded border border-border bg-white p-3 font-mono text-[11px] leading-tight text-black shadow-sm">
      <div ref={logoHost} className="mb-2" />
      <div className="text-center text-[13px] font-bold uppercase">{storeName}</div>
      {addressLine && <div className="text-center">{addressLine}</div>}
      {cityLine && <div className="text-center">{cityLine}</div>}
      <div className="my-1 border-t border-dashed border-black/40" />
      <div className="flex justify-between">
        <span>Register</span>
        <span>Lane 1</span>
      </div>
      <div className="flex justify-between">
        <span>Cashier</span>
        <span>Sample</span>
      </div>
      <div className="my-1 border-t border-dashed border-black/40" />
      <div className="flex justify-between font-semibold">
        <span>Item</span>
        <span>Total</span>
      </div>
      <div className="flex justify-between">
        <span>Sample Product</span>
        <span>4.99</span>
      </div>
      <div className="pl-3 text-black/70">2 @ 2.50 ea</div>
      <div className="flex justify-between">
        <span>Another Item</span>
        <span>12.00</span>
      </div>
      <div className="my-1 border-t border-dashed border-black/40" />
      <div className="flex justify-between">
        <span>Subtotal</span>
        <span>16.99</span>
      </div>
      <div className="flex justify-between">
        <span>Tax</span>
        <span>1.40</span>
      </div>
      <div className="flex justify-between text-[13px] font-bold">
        <span>TOTAL</span>
        <span>18.39</span>
      </div>
      <div className="mt-2 text-center">Thank you for shopping!</div>
    </div>
  );
}
